// CountryFlag - a country's flag. The one flag renderer in the dashboard.
//
// One pre-rasterized PNG sprite (flags/, built by scripts/build-flag-sprite.mjs
// from flag-icons), addressed by CSS background position. Not emoji: Windows
// renders a flag emoji as its two letters. Not per-flag SVG: the source files
// carry coats of arms up to 180 KB each, and a hundred of them in one list
// made the page fetch a hundred files and re-rasterize them while scrolling.

import { cn } from '@/lib/classnames';
import { FLAG_CODES } from './flags/flagCodes';
import './flags/flag-sprite.css';

/** Whether the sprite has a flag for an ISO 3166-1 alpha-2 code. A caller
 *  that must show something readable for a code without one checks this. */
export function hasFlag(code: string): boolean {
  return FLAG_CODES.has(code.toLowerCase());
}

/** A 16x12 flag. A code the sprite lacks draws an empty frame of the same
 *  size, so a list of names stays aligned. Decorative by default, because the
 *  country's name usually sits beside it. Pass `label` when the flag stands
 *  alone and has to name the country itself. */
export function CountryFlag({ code, label, className }: { code: string; label?: string; className?: string }) {
  const cc = code.toLowerCase();
  return (
    <span
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : 'true'}
      title={label}
      className={cn('inline-block w-4 h-3 shrink-0 ring-1 ring-rule', FLAG_CODES.has(cc) && `country-flag-${cc}`, className)}
    />
  );
}
